/**
 * Reconciliation-v2 shared enums.
 *
 * Kept in their own module (not in `reconciliation-v2-api-client.ts`) so that
 * `recon-v2-helpers.ts` can import them as real values without pulling in the
 * api-client — which instantiates axios at load and would break the helpers'
 * unit-test isolation. The api-client re-exports these for import-site
 * back-compat.
 */

/** Signed classification of a period_allocation slice (mirrors the backend enum). */
export enum AllocationClassification {
  CHARGE = 'CHARGE',
  REFUND_CANCELLATION = 'REFUND_CANCELLATION',
  REFUND_DEPENDENT_REMOVAL = 'REFUND_DEPENDENT_REMOVAL',
  REFUND_BILLING_CORRECTION = 'REFUND_BILLING_CORRECTION',
  REFUND_OTHER = 'REFUND_OTHER',
}

/** The refund-only subset of {@link AllocationClassification} (excludes CHARGE). */
export type RefundReason =
  | AllocationClassification.REFUND_CANCELLATION
  | AllocationClassification.REFUND_DEPENDENT_REMOVAL
  | AllocationClassification.REFUND_BILLING_CORRECTION
  | AllocationClassification.REFUND_OTHER

/** Rail a manual refund is recorded against. */
export enum ManualRefundSource {
  GroupAch = 'group_ach',
  Check = 'check',
}

/**
 * Why an unposted transaction is sitting in the Required-action queue. Sent
 * verbatim as the `filter` query param on the `kind=transaction` arm.
 */
export enum ReviewReason {
  Refund = 'refund',
  UnresolvedBenefit = 'unresolved_benefit',
  // Benefit resolved but tx is outside its coverage window. (In-period resolved
  // txs are the sweep's job and aren't surfaced in the queue.)
  AutoAllocateSkipped = 'auto_allocate_skipped',
  // Group ACH / check charges — the auto-allocator never places these; each is
  // surfaced as required action under its own filter so an operator allocates it
  // at their discretion.
  GroupAch = 'group_ach',
  Check = 'check',
}

/** Discrepancy-issue kind surfaced in the UI (lowercase, matches the API contract). */
export enum ReconIssueType {
  Overpaid = 'overpaid',
  Underpaid = 'underpaid',
  ManualReview = 'manual_review',
}

/** How much of a transaction was absorbed (written off) — mirrors the backend enum. */
export enum AbsorptionType {
  /** The whole transaction (e.g. a duplicate payment). */
  Full = 'FULL',
  /** Only the excess portion (a partial overpay). */
  Partial = 'PARTIAL',
}

/**
 * Why a carrier charged less than the expected price — mirrors the backend enum
 * `ExplicitCreditReason`. The values are spelled identically to a subset of
 * funding-invoice's `CreditReason` so a credit maps 1:1 when the invoicing side
 * consumes it; keep them in lockstep with
 * `enrollment-platform/.../financeDB/recon-v2.enums.ts`.
 */
export enum ExplicitCreditReason {
  CARRIER_REBATE = 'CARRIER_REBATE',
  PREMIUM_CORRECTION = 'PREMIUM_CORRECTION',
  BILLED_TWICE = 'BILLED_TWICE',
  OTHER = 'OTHER',
}

/**
 * Which arm of the Required-action queue a request wants. The query param uses
 * the operator-facing word `discrepancy`; the ROW discriminator on the response
 * keeps its original wire value `'issue'` (renaming either is breaking on the
 * EPS side). Do not conflate the two.
 */
export type ReviewQueueKind = 'transaction' | 'discrepancy'

/**
 * The `pullDays` list-filter value that selects benefits with no autopay pull
 * date. Mirrors EPS `PULL_DAY_NOT_SET` (@surecompanies/contracts
 * reconciliation-v2, newer than the version installed here).
 */
export const PULL_DAY_NOT_SET = 'not_set'

/** One `pullDays` filter value: a day of month 1–31, or {@link PULL_DAY_NOT_SET}. */
export type PullDayFilter = number | typeof PULL_DAY_NOT_SET
